/**
 * REVIEW-FORM / R1 — in-app "Rate ArogyaKhata" (FE-1). Binds to the FROZEN
 * §C1/§C2 API CONTRACT (state/epics/REVIEW-FORM/build/build-plan.md), not to
 * built backend code — the backend is being written in parallel.
 *
 * `POST /api/organisation/reviews` body is `extra="forbid"` server-side —
 * the request carries EXACTLY the three allowed keys, never `org_id`,
 * `user_id`, `user_name`, `role`, `submitted_at`, or `consent_text_version`
 * (all server-stamped; `consent_text_version` is now stamped server-side
 * from `CONSENT_TEXT_VERSION` and REJECTED with 422 if the client sends it).
 *
 * `fetchOrgName()` reads the org's display name from the branding config —
 * never throws, resolves `null` on any failure so the caller's identity
 * block can fall back to "Your organisation" without blocking submit.
 *
 * CONTRACT RECONCILE (integration, 2026-09-11): the build plan's D7 assumed
 * a `GET /api/organisation/profile`. The backend exposes only `POST` on that
 * path (the GET would 405), so the name comes from
 * `GET /api/organisation/prescription-config` → `config.clinic_name` — the
 * same value the app already prints on prescriptions/tokens, readable by
 * every org role (`require_org_user`, no permission slug).
 */

import apiClient from './apiClient';

const REVIEWS_PATH = '/api/organisation/reviews';
const ORG_BRANDING_PATH = '/api/organisation/prescription-config';
const SUBMIT_TIMEOUT_MS = 10000;

/**
 * Customer-language text for a server-side 422, keyed by the failing field.
 * The client already blocks all three of these before Submit enables, so a
 * 422 in practice means a drift — the copy stays readable either way.
 */
const VALIDATION_MESSAGES = {
  rating: 'Please pick a rating between 1 and 5 stars.',
  review: 'Your review needs to be between 20 and 600 characters.',
  consent_publish: 'Please tick the consent box before sending.',
  default: 'Please check your rating and review, then try again.',
};

/** Same detail-shape translation convention as diagWaiverApi.js / monthlyReportsApi.js. */
function parseError(error, fallback) {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;

  switch (status) {
    case 401:
      return { error: 'Session expired. Please login again.', status };
    case 403:
      return { error: 'Only organisation accounts can send a review.', status };
    case 422: {
      // §C1 body is `{"detail":[{"loc":["body","rating"],"msg":"…"}]}`.
      // CONTRACT RECONCILE (integration): the real `msg` strings are raw
      // pydantic text ("Value error, consent_publish must be true",
      // "Extra inputs are not permitted") — developer language, never shown
      // to a user. The field that failed IS surfaced, in customer words.
      const field = Array.isArray(detail) ? detail[0]?.loc?.[1] : null;
      return { error: VALIDATION_MESSAGES[field] || VALIDATION_MESSAGES.default, status };
    }
    default:
      return { error: fallback, status };
  }
}

/**
 * §C1 POST /api/organisation/reviews — submit a review. Body is EXACTLY
 * `{ rating, review, consent_publish }`, never any other key — the server
 * now stamps `consent_text_version` itself and 422s if the client sends it.
 *
 * Response (passed through as `data`, untouched) on 201:
 * `{ id, org_id, org_name, user_id, user_name, role, rating, review,
 *    consent_publish, consent_text_version, submitted_at, status, source }`
 */
export const submitReview = async ({ rating, review, consent_publish } = {}) => {
  try {
    const response = await apiClient.post(
      REVIEWS_PATH,
      { rating, review, consent_publish },
      { timeout: SUBMIT_TIMEOUT_MS },
    );
    return { success: true, data: response.data, status: response.status };
  } catch (error) {
    console.error('[ReviewsAPI] submitReview error:', error);
    return { success: false, ...parseError(error, 'We could not send your review.') };
  }
};

/**
 * D7 — the org display name is NOT in the stored session, so the identity
 * block does one guarded GET here. Never throws; `null` means "unknown",
 * the caller falls back to "Your organisation".
 */
export const fetchOrgName = async () => {
  try {
    const response = await apiClient.get(ORG_BRANDING_PATH);
    return response?.data?.config?.clinic_name || null;
  } catch (error) {
    console.warn('[ReviewsAPI] fetchOrgName failed:', error?.message);
    return null;
  }
};
